//src/pages/Presentation/LazyVideoPreview.tsx
import React, { useEffect, useRef, useState } from "react";
import { HLSPlayer } from "./HlsPlayer";

type LazyVideoPreviewProps = {
  src: string;
  poster?: string | null;
  style?: React.CSSProperties;
  rootMargin?: string;
  keepMountedOnceVisible?: boolean;
  videoId: string;
};

export default function LazyVideoPreview({
  src,
  poster,
  style = {},
  rootMargin = "300px 0px",
  keepMountedOnceVisible = false,
  videoId,
}: LazyVideoPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsVisible(visible);

        if (visible) {
          setHasBeenVisible(true);
          if (keepMountedOnceVisible) {
            observer.disconnect();
          }
        } else if (!keepMountedOnceVisible) {
          setVideoReady(false);
        }
      },
      {
        root: null,
        rootMargin,
        threshold: 0.01,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, keepMountedOnceVisible]);

  const shouldRenderPlayer = keepMountedOnceVisible
    ? hasBeenVisible
    : isVisible;

  return (
    <div
      ref={containerRef}
      data-video-id={videoId}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {shouldRenderPlayer && (
       <HLSPlayer
  src={src}
  poster={poster || undefined}
  muted={true}
  videoId={videoId}
  style={style}
  onReady={() => setVideoReady(true)}
/>
      )}

      {poster && (!shouldRenderPlayer || !videoReady) && (
        <img
          src={poster}
          alt="Video preview"
          loading="lazy"
          decoding="async"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            transition: "opacity 180ms ease",
            opacity: videoReady ? 0 : 1,
            pointerEvents: "none",
            ...style,
          }}
        />
      )}

      {!poster && !videoReady && !shouldRenderPlayer && (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "#111",
            ...style,
          }}
        />
      )}
    </div>
  );
}